import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FAQQuestionCard } from './FAQQuestionCard';
import { CreateQuestionModal } from './CreateQuestionModal';
import { QuestionDetailModal } from './QuestionDetailModal';
import { supabase } from '../../lib/supabase';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import toast from 'react-hot-toast';

export interface FAQQuestion {
  id: number;
  league_id: number;
  user_id: number;
  username: string;
  question: string;
  category: string;
  priority: string;
  status: string;
  is_pinned: boolean;
  views_count: number;
  answer_count: number;
  created_at: string;
  updated_at: string;
  has_official_answer: boolean;
}

export interface FAQAnswer {
  id: number;
  question_id: number;
  user_id: number;
  username: string;
  answer: string;
  is_official: boolean;
  created_at: string;
  updated_at: string;
}

export function FAQPage() {
  const navigate = useNavigate();
  const { selectedLeague } = useLeagueStore();
  const { user_id } = useUserStore();
  const [questions, setQuestions] = useState<FAQQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<FAQQuestion | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { value: 'all', label: 'Toutes les catégories' },
    { value: 'general', label: 'Général' },
    { value: 'rules', label: 'Règlements' },
    { value: 'trades', label: 'Échanges' },
    { value: 'draft', label: 'Repêchage' },
    { value: 'salary', label: 'Salaires' },
    { value: 'technical', label: 'Technique' }
  ];

  const statuses = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'open', label: 'Ouvertes' },
    { value: 'answered', label: 'Répondues' },
    { value: 'closed', label: 'Fermées' }
  ];

  const priorities = [
    { value: 'normal', label: 'Normal', color: 'success' },
    { value: 'high', label: 'Élevée', color: 'warning' },
    { value: 'urgent', label: 'Urgente', color: 'danger' }
  ];

  useEffect(() => {
    if (selectedLeague?.id) {
      loadQuestions();
    }
  }, [selectedLeague]);

  const loadQuestions = async () => {
    if (!selectedLeague?.id) return;
    
    try {
      setLoading(true);
      
      // Requête directe avec JOIN
      const { data, error } = await supabase
        .from('faq_questions')
        .select(`
          *,
          users!faq_questions_user_id_fkey(username),
          faq_answers(id, is_official)
        `)
        .eq('league_id', selectedLeague.id)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transformer les données
      const transformedData = (data || []).map((item: any) => ({
        id: item.id,
        league_id: item.league_id,
        user_id: item.user_id,
        username: item.users?.username || 'Utilisateur inconnu',
        question: item.question,
        category: item.category,
        priority: item.priority,
        status: item.status,
        is_pinned: item.is_pinned,
        views_count: item.views_count,
        answer_count: item.faq_answers?.length || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
        has_official_answer: item.faq_answers?.some((answer: any) => answer.is_official) || false
      }));

      setQuestions(transformedData);
    } catch (error) {
      console.error('Error loading FAQ questions:', error);
      toast.error('Erreur lors du chargement des questions');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionCreated = () => {
    setShowCreateModal(false);
    loadQuestions();
    toast.success('Question créée avec succès!');
  };

  const handleQuestionUpdated = () => {
    loadQuestions();
    setSelectedQuestion(null);
  };

  const filteredQuestions = questions.filter(question => {
    const matchesStatus = filterStatus === 'all' || question.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || question.category === filterCategory;
    const matchesSearch = !searchTerm || 
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.username.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  // Separate pinned and regular questions
  const pinnedQuestions = filteredQuestions.filter(q => q.is_pinned);
  const regularQuestions = filteredQuestions.filter(q => !q.is_pinned);

  if (!selectedLeague) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <h3>Veuillez sélectionner une ligue pour voir les FAQ</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          {/* Bouton de retour */}
          <div className="mb-3">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/my-office')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Retour au menu principal
            </button>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Foire aux Questions</h2>
              <p className="text-muted mb-0">
                Posez vos questions et obtenez des réponses du commissaire
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus me-2"></i>
              Poser une question
            </button>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Recherche</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Catégorie</label>
                  <select
                    className="form-select"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={() => {
                      setSearchTerm('');
                      setFilterCategory('all');
                      setFilterStatus('all');
                    }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Effacer
                  </button>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Pinned Questions */}
              {pinnedQuestions.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3">
                    <i className="fas fa-thumbtack me-2 text-warning"></i>
                    Questions épinglées
                  </h5>
                  <div className="row">
                    {pinnedQuestions.map(question => (
                      <div key={question.id} className="col-12 mb-3">
                        <FAQQuestionCard
                          question={question}
                          onClick={() => setSelectedQuestion(question)}
                          priorities={priorities}
                          categories={categories}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Questions */}
              {regularQuestions.length > 0 ? (
                <div>
                  {pinnedQuestions.length > 0 && (
                    <h5 className="mb-3">Autres questions</h5>
                  )}
                  <div className="row">
                    {regularQuestions.map(question => (
                      <div key={question.id} className="col-12 mb-3">
                        <FAQQuestionCard
                          question={question}
                          onClick={() => setSelectedQuestion(question)}
                          priorities={priorities}
                          categories={categories}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : pinnedQuestions.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                  <h4>Aucune question trouvée</h4>
                  <p className="text-muted">
                    Soyez le premier à poser une question!
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateQuestionModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onQuestionCreated={handleQuestionCreated}
        categories={categories.filter(c => c.value !== 'all')}
        priorities={priorities}
      />

      {selectedQuestion && (
        <QuestionDetailModal
          question={selectedQuestion}
          show={true}
          onHide={() => setSelectedQuestion(null)}
          onQuestionUpdated={handleQuestionUpdated}
          categories={categories.filter(c => c.value !== 'all')}
          priorities={priorities}
        />
      )}
    </div>
  );
}
